import {useCallback, useReducer, useState} from 'react';
import {toast} from 'react-toastify';
import {useParams} from 'react-router-dom';
import {cacheUpdateItem} from 'utils';
import {Button, ButtonBack, Divider, Transitioner} from 'components';
import {
  getGetPaymentCardsQueryKey,
  useCreatePaymentCard,
  useDeletePaymentCard,
  useGetPaymentCards,
  useSetDefaultPaymentCard,
  type PaymentCardType,
} from 'api/generated';

import PaymentCardItem from './PaymentCard.Item';
import ModalPaymentCardUpdate from './Modal.PaymentCard.Update';

interface IPaymentCardsMineProps {}

const PaymentCardsMine = ({}: IPaymentCardsMineProps) => {
  const params = useParams();
  const workspace = params.workspace ?? '';

  const {data: cardsData, refetch: fetchCards} = useGetPaymentCards({query: {staleTime: 5 * 1000}});
  const cards = cardsData?.data?.data ?? [];

  const [showModal, toggleModal] = useReducer(s => !s, false);
  const [cardToUpdate, setCardToUpdate] = useState<PaymentCardType>();

  const createPaymentCard = useCreatePaymentCard();
  const handleAdd = async () => {
    const {data: res} = await createPaymentCard.mutateAsync();
    if (res.data?.success) {
      const url = res.data.message;
      window.location.href = url;
    } else if (res.data?.message) {
      toast.error(res.data.message);
    } else if (res.errors) {
      toast.error(res.errors[0].detail);
    }
  };

  const handleEdit = useCallback((card: PaymentCardType) => {
    setCardToUpdate(card);
    toggleModal();
  }, []);

  const setDefaultPaymentCard = useSetDefaultPaymentCard();
  const handleSetDefault = useCallback(
    async (card: PaymentCardType) => {
      const {data: res} = await setDefaultPaymentCard.mutateAsync({paymentCardUid: card.uid});
      if (res.data) {
        // Optimistic update
        for (const card of cards) {
          const isDefault = card.uid === res.data.uid ? true : false;
          cacheUpdateItem({
            queryKey: getGetPaymentCardsQueryKey(),
            data: {...card, default: isDefault},
          });
        }
      } else if (res.errors) {
        toast.error(res.errors[0].detail);
      }
    },
    [cards],
  );

  const deletePaymentCard = useDeletePaymentCard();
  const handleDelete = useCallback(
    async (card: PaymentCardType) => {
      const {data: res} = await deletePaymentCard.mutateAsync({uid: card.uid});
      if (res.data) {
        // Optimistic update
        cacheUpdateItem({
          queryKey: getGetPaymentCardsQueryKey(),
          data: res.data,
          remove: true,
        });
        await new Promise(resolve => setTimeout(resolve, 2000));
        fetchCards();
      } else if (res.errors) {
        toast.error(res.errors[0].detail);
      }
    },
    [fetchCards],
  );

  return (
    <>
      <Transitioner>
        <ButtonBack url={`/${workspace}/billing`} />
        <Divider />
        <div className="flex items-center justify-between">
          <div className="sm:flex-auto">
            <h1 className="font-medium text-lg dark:text-white">Manage payment cards</h1>
            <p className="mt-2 text-sm text-gray-700 dark:text-darkText hidden sm:block">
              Recurring charges will be billed to your default payment card.
            </p>
          </div>
          <Button text="+ Add card" size="small" onClick={handleAdd} loading={createPaymentCard.isPending} />
        </div>
        <Transitioner delay={0.1}>
          <div className="py-6 flex flex-col gap-3">
            {cards.map(card => {
              return <PaymentCardItem key={card.uid} card={card} onEdit={handleEdit} onSetDefault={handleSetDefault} onDelete={handleDelete} />;
            })}
          </div>
        </Transitioner>
      </Transitioner>
      <ModalPaymentCardUpdate visible={showModal} onClose={toggleModal} card={cardToUpdate} />
    </>
  );
};

export default PaymentCardsMine;
